import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import { FiX, FiPaperclip, FiMic, FiArrowRight, FiMinus } from "react-icons/fi";
import qpLogo from "./assets/qpLogo.png";
import { useNavigate } from "react-router-dom";
import PersonIcon from "@mui/icons-material/Person"; // ← MUI icon
import CheckIcon from "@mui/icons-material/CheckCircle"; // ✅ icon

const PillGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 12px;
  margin: 8px 0 16px;
`;

const Pill = styled.button`
  padding: 14px 18px;
  border-radius: 9999px;
  font-weight: 500;
  font-size: 15px;
  border: 2px solid #144a8c;
  background: ${({ $active }) => ($active ? "#e6ecff" : "transparent")};
  color: #144a8c;
  cursor: pointer;
  display: flex; /* to align the ✅ */
  align-items: center;
  justify-content: center;
  gap: 6px;

  svg {
    /* green check‑mark */
    opacity: ${({ $active }) => ($active ? 1 : 0)};
    transition: opacity 0.2s;
  }
`;

const ApplyBtn = styled.button`
  align-self: flex-end;
  background: #144a8c;
  color: #fff;
  padding: 10px 18px;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
`;

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
`;
const Overlay = styled.div`
  position: fixed;
  top: 18px;
  left: 18px;
  width: calc(100vw - 36px);
  height: calc(100vh - 36px);
  background: linear-gradient(180deg, #ffffff 0%, #f0ebfe 54.39%, #e3dbfe 100%);
  border-radius: 15px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  z-index: 1000;
`;
const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
`;
const LogoFull = styled.img`
  height: 32px;
`;

// ——— Card-selection styles ———
const TitleSection = styled.div`
  text-align: center;
  padding: 24px 16px;
`;
const Greeting = styled.div`
  font-size: 14px;
  color: #666;
`;
const MainTitle = styled.h1`
  font-size: 32px;
  margin-top: 8px;
  & > span {
    color: #144a8c;
  }
`;
const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  padding: 0 16px;
  margin-bottom: 30%;
`;

const Card = styled.button`
  background: #fff;
  border: 2px solid ${({ active }) => (active ? "#144A8C" : "#e0e0e0")};
  border-radius: 12px;
  padding: 16px;
  text-align: left;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  &:hover {
    border-color: #144a8c;
  }
`;
const CardTitle = styled.div`
  display: flex;
  align-items: center;
  font-weight: 500;
`;
const CardDesc = styled.div`
  font-size: 12px;
  color: #666;
  margin-top: 4px;
`;

// ——— Chat styles ———
const MessagesList = styled.div`
  flex: 1;
  padding: 0 16px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
const Bubble = styled.div`
  max-width: 70%;
  padding: 12px;
  border-radius: 16px;
  background: ${({ role }) => (role === "user" ? "#144a8c" : "#fff")};
  color: ${({ role }) => (role === "user" ? "#fff" : "#000")};
  align-self: ${({ role }) => (role === "user" ? "flex-end" : "flex-start")};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

// ——— Shared input bar ———
const BottomBar = styled.div`
  display: flex;
  align-items: center;
  width: 80%;
  margin: 16px auto 40px;
  padding: 12px 16px;
  border: 1px solid #8c8c8c1a;
  border-radius: 39px;
  background: #8c8c8c3d;
  backdrop-filter: blur(0px);
`;
const AttachBtn = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  margin-right: 8px;
  cursor: pointer;
`;
const InputFull = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-size: 14px;
  padding: 8px 12px;
  border-radius: 20px;
  background-color: transparent;
`;
const MicBtn = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  margin: 0 8px;
  cursor: pointer;
`;
const SendBtn = styled.button`
  background: #144a8c;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const MinimizeBtn = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  margin-right: 12px;
  cursor: pointer;
  margin-left: auto;
`;

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 8px;
  gap: 8px;

  /* ← flip order when it’s a user row  */
  justify-content: ${({ $isUser }) => ($isUser ? "flex-end" : "flex-start")};
`;

const Chip = styled.div`
  min-width: 24px;
  height: 24px;
  border-radius: 50%;
  font-size: 12px;
  font-weight: 600;
  margin-top: 10px;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;

  background: ${({ role }) => (role === "assistant" ? "#144A8C" : "#8c8c8c")};
`;

const LawyerCardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;
  padding: 16px;
  margin: 4px 0;
`;
const LawyerCard = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  align-items: start;
`;
const Avatar = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  margin-bottom: 8px;
`;
const Name = styled.div`
  font-weight: 600;
  margin-bottom: 4px;
`;
const Spec = styled.div`
  font-size: 12px;
  color: #555;
  margin-bottom: 4px;
`;
const Rating = styled.div`
  font-size: 12px;
  color: #f5a623;
  margin-bottom: 4px;
`;
const Location = styled.div`
  font-size: 12px;
  color: #666;
  margin-bottom: 8px;
`;
const Availability = styled.div`
  font-size: 12px;
  color: #144a8c;
  margin-bottom: 8px;
`;
const BookBtn = styled.button`
  background: #144a8c;
  color: #fff;
  border: none;
  padding: 8px;
  border-radius: 8px;
  width: 100%;
  cursor: pointer;
`;

const Disclaimer = styled.div`
  font-size: 10px;
  color: #999;
  font-family: Poppins;
  padding: 0 16px;
  margin-top: 8px;
  line-height: 1.2;
`;

export const LawyerGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;
  width: 100%;
`;

const DebugInfo = styled.div`
  font-size: 10px;
  color: #999;
  background-color: #f0f0f0;
  padding: 4px;
  margin: 4px 16px;
  border-radius: 4px;
  display: ${process.env.NODE_ENV === "development" ? "block" : "none"};
`;

const RefineButton = styled.button`
  background: #144a8c;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 8px;
  margin: 0 16px 16px;
  cursor: pointer;
  font-weight: 500;
  align-self: flex-end;
`;

function PillFilterPanel({ onSubmit, onCancel }) {
  const [state, setState] = React.useState({
    experience: false,
    affordability: false,
    gender: false,
    outcomes: false,
    communicator: false,
    other: false,
  });

  const toggle = (key) => setState((s) => ({ ...s, [key]: !s[key] }));

  const buildFilters = () => {
    const filters = {};
    if (state.experience) filters.experience = ">=10 years";
    if (state.gender) filters.gender = "female";
    if (state.affordability) filters.affordability = "very_affordable";
    if (state.other) filters.other = "custom";
    // you can map outcomes / communicator however you like
    return filters;
  };

  return (
    <>
      <div style={{ fontSize: 12, color: "#666", margin: "4px 0 4px" }}>
        (Please select all that apply)
      </div>

      <PillGrid>
        <Pill $active={state.experience} onClick={() => toggle("experience")}>
          At least 10+ years of experience
          <CheckIcon fontSize="small" />
        </Pill>

        <Pill
          $active={state.affordability}
          onClick={() => toggle("affordability")}
        >
          Must be very affordable
          <CheckIcon fontSize="small" />
        </Pill>

        <Pill $active={state.gender} onClick={() => toggle("gender")}>
          I prefer a female lawyer
          <CheckIcon fontSize="small" />
        </Pill>

        <Pill $active={state.outcomes} onClick={() => toggle("outcomes")}>
          Focus on winning rate / outcomes
          <CheckIcon fontSize="small" />
        </Pill>

        <Pill
          $active={state.communicator}
          onClick={() => toggle("communicator")}
        >
          Clear communicator, explains things well
          <CheckIcon fontSize="small" />
        </Pill>

        <Pill $active={state.other} onClick={() => toggle("other")}>
          Other (please describe)
          <CheckIcon fontSize="small" />
        </Pill>
      </PillGrid>

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button
          onClick={onCancel}
          style={{ background: "none", border: "none", cursor: "pointer" }}
        >
          Cancel
        </button>
        <ApplyBtn onClick={() => onSubmit(buildFilters())}>
          See Better Matches
        </ApplyBtn>
      </div>
    </>
  );
}

function LawyerCardBlock({ lawyers }) {
  const navigate = useNavigate();
  if (!lawyers || lawyers.length === 0) return null;

  return (
    <>
      <LawyerCardsContainer>
        <LawyerGrid>
          {lawyers.map((L) => (
            <LawyerCard key={L.user_id}>
              <Avatar
                src={L.profile_photo_url || "https://via.placeholder.com/48"}
              />
              <Name>{L.name}</Name>

              <Spec>
                {L.years_of_experience} yrs •{" "}
                {Array.isArray(L.practice_areas)
                  ? L.practice_areas[0]
                  : L.practice_area}
              </Spec>

              <Rating>
                ⭐ {Number(L.average_rating || 0).toFixed(2)} •{" "}
                {L.number_of_reviews || 0} reviews
              </Rating>

              <Location>
                📍 {L.city}, {L.state}
              </Location>

              <Availability>Next Available Today</Availability>

              <BookBtn onClick={() => navigate(`/lawyer-detail/${L.user_id}`)}>
                Book online
              </BookBtn>
            </LawyerCard>
          ))}
        </LawyerGrid>
      </LawyerCardsContainer>
      <Disclaimer>
        Please Note: All information you provide is confidential. These
        conversations are not legal advice.
      </Disclaimer>
    </>
  );
}

export default function ChatWindow({
  options,
  selectedCard,
  lawyerCards,
  hasSentFirst,
  messages,
  inputValue,
  onCardSelect,
  onInputChange,
  onSend,
  onClose,
  onMinimize,
  lastIds,
  showFilters,
  setShowFilters,
}) {
  useEffect(() => {
    console.log("ChatWindow received lawyerCards:", lawyerCards);
    // If you have lawyerCards, make sure they're visible in the DOM
    if (lawyerCards && lawyerCards.length > 0) {
      console.log(
        "Lawyers to display:",
        lawyerCards.map((l) => l.name).join(", ")
      );
    }
  }, [lawyerCards]);
  const endRef = useRef(null);
  const navigate = useNavigate();

  // scroll on new messages
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Debug log when lawyerCards changes
  useEffect(() => {
    console.log("ChatWindow received lawyerCards:", lawyerCards);
  }, [lawyerCards]);

  // Refine matches handler
  const handleRefineMatches = () => {
    // Get lawyer IDs from the current cards
    if (lawyerCards && lawyerCards.length > 0) {
      // const lawyer_ids = lawyerCards.map((lawyer) => lawyer.user_id);
      const lawyer_ids = lastIds;
      // Send a message to refine (will trigger the refine_lawyers function)
      onInputChange(
        JSON.stringify({
          refinement_request: true,
          lawyer_ids: lawyer_ids,
          filters: {},
        })
      );
      onSend();
    }
  };

  return (
    <>
      <Backdrop onClick={onClose} />
      <Overlay>
        <Header>
          <LogoFull src={qpLogo} alt="Logo" />{" "}
          <MinimizeBtn onClick={onMinimize} aria-label="Minimize">
            <FiMinus />{" "}
          </MinimizeBtn>
          <FiX onClick={onClose} style={{ cursor: "pointer", fontSize: 24 }} />
        </Header>

        {!hasSentFirst ? (
          // 1) Card-selection flow
          <>
            <TitleSection>
              <Greeting>
                Hi, I'm QuidBot, your personal legal assistant
              </Greeting>
              <MainTitle>
                How can I <span>assist</span> you today?
              </MainTitle>
            </TitleSection>

            <CardsGrid>
              {options.map((o) => (
                <Card
                  key={o.key}
                  active={o.key === selectedCard}
                  onClick={() => onCardSelect(o.key)}
                >
                  <CardTitle>
                    {o.icon}
                    <span style={{ marginLeft: 8 }}>{o.title}</span>
                  </CardTitle>
                  <CardDesc>{o.desc}</CardDesc>
                </Card>
              ))}
            </CardsGrid>

            <BottomBar>
              <AttachBtn>
                <FiPaperclip />
              </AttachBtn>
              <InputFull
                value={inputValue}
                onChange={(e) => onInputChange(e.target.value)}
                placeholder="Type your first message…"
                onKeyDown={(e) => e.key === "Enter" && onSend()}
              />
              <MicBtn>
                <FiMic />
              </MicBtn>
              <SendBtn onClick={onSend}>
                <FiArrowRight />
              </SendBtn>
            </BottomBar>
          </>
        ) : (
          // 2) Chat flow
          <>
            <MessagesList>
              {messages.map((m, i) => {
                if (m.role === "cards") {
                  return (
                    <React.Fragment key={i}>
                      {/* card grid */}
                      <LawyerCardBlock lawyers={m.payload} />

                      {/* filters OR button, driven by lifted flag */}
                      {showFilters ? (
                        <PillFilterPanel
                          onCancel={() => setShowFilters(false)}
                          onSubmit={(filters) => {
                            setShowFilters(false); // close panel
                            onInputChange(JSON.stringify(filters)); // JSON‑only msg
                            onSend(); // triggers refine flow
                          }}
                        />
                      ) : (
                        <RefineButton onClick={() => setShowFilters(true)}>
                          See Better Matches
                        </RefineButton>
                      )}
                    </React.Fragment>
                  );
                }

                return (
                  <React.Fragment key={i}>
                    <Row $isUser={m.role === "user"}>
                      {m.role === "assistant" && (
                        <Chip role="assistant">QB</Chip>
                      )}

                      <Bubble role={m.role}>{m.content}</Bubble>

                      {m.role === "user" && (
                        <Chip role="user">
                          <PersonIcon style={{ fontSize: 16 }} />
                        </Chip>
                      )}
                    </Row>

                    {m.role === "assistant" && (
                      <Disclaimer>
                        Please Note: All information you provide is
                        confidential. These conversations are not legal advice.
                      </Disclaimer>
                    )}
                  </React.Fragment>
                );
              })}
              <div ref={endRef} />
            </MessagesList>

            {process.env.NODE_ENV === "development" && (
              <DebugInfo>
                lawyerCards state:{" "}
                {lawyerCards
                  ? `${lawyerCards.length} lawyers available`
                  : "null"}
              </DebugInfo>
            )}

            <BottomBar>
              <AttachBtn>
                <FiPaperclip />
              </AttachBtn>
              <InputFull
                placeholder="Type your message…"
                value={inputValue}
                onChange={(e) => onInputChange(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && onSend()}
              />
              <MicBtn>
                <FiMic />
              </MicBtn>
              <SendBtn onClick={onSend}>
                <FiArrowRight />
              </SendBtn>
            </BottomBar>
          </>
        )}
      </Overlay>
    </>
  );
}
